import React, { useState, useEffect } from 'react';
import { Tabs, Button, Space, Modal, Input, Typography, Menu, Dropdown, theme } from 'antd';
import type { MenuProps } from 'antd';
import type { DropResult } from '@hello-pangea/dnd';
import {
  PlusOutlined,
  EditOutlined,
  EllipsisOutlined,
  FileOutlined,
  HomeOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { createGlobalStyle } from 'styled-components';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';
import HopCanvasViewer from './viewers/HopCanvasViewer';
import MarkdownViewer from './viewers/MarkdownViewer';
import FileSummaryViewer from './viewers/FileSummaryViewer';
import useTabManagerStore from '../store/tabManagerStore';
import { usePipelineActions, useMarkdownActions, useMarkdowns, useSummaryActions, useSummaries, useFlowActions } from '../store/store';
import { createContextLogger } from '../utils/logger';

const logger = createContextLogger('TabManager');
import PipelineIcon from '../assets/icons/pipeline.svg?react';
import WorkflowIcon from '../assets/icons/workflow.svg?react';
import WelcomeViewer from './viewers/WelcomeViewer';

// Type definitions
interface TabManagerProps {
  showWelcome: boolean;
  onShowWelcomeChange: (show: boolean) => void;
}

interface Tab {
  id: string;
  type: string;
  name: string;
  isModified?: boolean; // Add isModified flag
}

// Global styles to fix tab content dimensions
const TabsGlobalStyle = createGlobalStyle`
  .ant-tabs-content, .ant-tabs-content-top {
    height: 100%;
    flex: 1;
    position: relative;
  }
  
  .ant-tabs, .ant-tabs-top {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  
  .ant-tabs-content-holder {
    flex: 1;
    position: relative;
    height: 100%;
  }
  
  .ant-tabs-tabpane {
    height: 100%;
  }

  .ant-tabs-nav {
    margin: 0 !important;
  }

  .ant-tabs-tab {
    margin: 0 !important;
    padding: 8px 16px !important;
    transition: background-color 0.3s;
  }

  .ant-tabs-tab:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  .ant-tabs-tab-active {
    /* Background color managed by Ant Design theme */
  }
  
`;

const tabsContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  position: 'relative',
};

const tabContentStyle: React.CSSProperties = {
  height: '100%',
  width: '100%',
  position: 'relative',
};

const TabManager: React.FC<TabManagerProps> = ({ showWelcome, onShowWelcomeChange }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const [isRenameModalVisible, setIsRenameModalVisible] = useState<boolean>(false);
  const [tabToRename, setTabToRename] = useState<Tab | null>(null);
  const [newTabName, setNewTabName] = useState<string>('');
  const [contextMenuTab, setContextMenuTab] = useState<Tab | null>(null);

  // Hide welcome overlay when a file is opened from the file browser
  useEffect(() => {
    const hideWelcome = (): void => onShowWelcomeChange(false);
    window.addEventListener('hideWelcomeOverlay', hideWelcome);
    return () => window.removeEventListener('hideWelcomeOverlay', hideWelcome);
  }, [onShowWelcomeChange]);

  // Get state and actions from tabManagerStore
  const { tabs, activeTabId, openTab, closeTab, setActiveTab, clearActiveTab, updateTabName, updateTabModified, reorderTabs } =
    useTabManagerStore((state) => ({
      tabs: state.tabs,
      activeTabId: state.activeTabId,
      openTab: state.openTab,
      closeTab: state.closeTab,
      setActiveTab: state.setActiveTab,
      clearActiveTab: state.clearActiveTab,
      updateTabName: state.updateTabName,
      updateTabModified: state.updateTabModified,
      reorderTabs: state.reorderTabs,
    }));

  // Get pipeline actions
  const { addPipeline, setActivePipeline, closePipeline, invalidatePipelineTransformCache } = usePipelineActions();

  // Get flow actions for cleanup
  const { clearNodeSelectionState, clearPipelineHistory } = useFlowActions();

  // Get markdown actions and state
  const { addMarkdown, deleteMarkdown } = useMarkdownActions();
  const markdowns = useMarkdowns();
  const { addSummary, deleteSummary } = useSummaryActions();
  const summaries = useSummaries();

  // Handle tab close with storage cleanup
  const handleTabClose = (tabId: string): void => {
    // Remove from localStorage if it's a pipeline or workflow
    const tab = tabs.find((t) => t.id === tabId);
    if (tab) {
      if (tab.type === 'pipeline') {
        // Invalidate transform cache for all transforms in this pipeline
        invalidatePipelineTransformCache(tabId);

        // Clear history for this pipeline
        clearPipelineHistory(tabId);

        // Remove from localStorage
        localStorage.removeItem(`pipeline_${tab.name}`);

        // Close the pipeline in the store
        closePipeline(tabId);

        // If this was the active pipeline, clear the active pipeline
        if (activeTabId === tabId) {
          // Clear active pipeline by setting it to an empty string or handle differently
          // For now, we'll leave it as is since setActivePipeline expects a string
        }
      } else if (tab.type === 'workflow') {
        // Workflows are stored in the same pipelines array as pipelines
        // Invalidate transform cache for all actions in this workflow
        invalidatePipelineTransformCache(tabId);

        // Clear history for this workflow
        clearPipelineHistory(tabId);

        // Remove from localStorage (workflows may use workflow_ prefix)
        localStorage.removeItem(`workflow_${tab.name}`);

        // Close the workflow in the store (same method as pipeline)
        closePipeline(tabId);

        // If this was the active workflow, clear the active pipeline
        if (activeTabId === tabId) {
          // Clear active pipeline by setting it to an empty string or handle differently
          // For now, we'll leave it as is since setActivePipeline expects a string
        }
      } else if (tab.type === 'markdown') {
        // Remove markdown from store
        deleteMarkdown(tabId);
      } else if (tab.type === 'summary') {
        // Remove summary from store
        deleteSummary(tabId);
      }

      logger.debug('Removed tab:', {
        tabId,
        type: tab.type,
        name: tab.name,
        wasActive: activeTabId === tabId,
      });
    }

    // Close the tab
    closeTab(tabId);
  };

  // Tab operations
  const handleAddTab = (type: string, id?: string | null, name?: string | null, path?: string): void => {
    const newId = id || `tab_${Date.now()}`;
    const tabName = name || t(`tabs.new${type.charAt(0).toUpperCase() + type.slice(1)}`);

    // Check if tab already exists
    const existingTab = tabs.find((tab) => tab.id === newId);
    if (existingTab) {
      // Switch to existing tab
      setActiveTab(newId);
      onShowWelcomeChange(false); // Hide welcome when switching to a tab
      if (type === 'pipeline') {
        setActivePipeline(newId);
      }
      logger.debug('Switched to existing tab:', {
        id: newId,
        type,
        name: tabName,
        timestamp: new Date().toISOString(),
      });
      return;
    }

    if (type === 'pipeline') {
      // Initialize a new pipeline in the store
      addPipeline(newId, tabName, 'pipeline');
    } else if (type === 'workflow') {
      // Initialize a new workflow in the store (same structure as pipeline)
      addPipeline(newId, tabName, 'workflow');
    } else if (type === 'markdown') {
      // Initialize a new markdown in the store
      addMarkdown(newId, tabName, '# New Document');
    } else if (type === 'summary') {
      // For summaries, we expect the summary data to already be in the store
      // This will be handled by the caller (FileBrowser) before calling openTab
    }

    // Add the tab to the store
    // Mark as new if no path is provided
    const isNew = !path;
    openTab(newId, type, tabName, undefined, path, isNew);
    
    // Mark new pipelines, workflows, and markdown as modified since they haven't been saved yet
    if (type === 'pipeline' || type === 'workflow' || type === 'markdown') {
      updateTabModified(newId, true);
    }
    
    onShowWelcomeChange(false); // Hide welcome when creating a new tab
    logger.debug('Created new tab:', {
      id: newId,
      type,
      name: tabName,
      path,
      timestamp: new Date().toISOString(),
    });
  };

  const handleTabChange = (activeKey: string): void => {
    const activeTab = tabs.find((tab) => tab.id === activeKey);
    if (activeTab) {
      logger.debug('Switching to tab:', {
        id: activeTab.id,
        type: activeTab.type,
        name: activeTab.name,
        timestamp: new Date().toISOString(),
      });
      setActiveTab(activeKey);
      onShowWelcomeChange(false); // Hide welcome when switching to a tab
      
      // Clear node selection state when switching tabs to prevent stale state
      clearNodeSelectionState();
      
      // Manage pipeline context based on tab type
      if (activeTab.type === 'pipeline' || activeTab.type === 'workflow') {
        setActivePipeline(activeKey);
      } else {
        // Clear active pipeline when switching to non-pipeline/workflow tabs
        // For now, we'll leave it as is since setActivePipeline expects a string
      }
    }
  };

  const handleContextMenu = (e: React.MouseEvent, tab: Tab): void => {
    e.preventDefault();
    setContextMenuTab(tab);
  };

  const handleCloseAll = (): void => {
    tabs.forEach((tab) => handleTabClose(tab.id));
  };

  const handleCloseAllButThis = (): void => {
    if (!contextMenuTab) return;
    tabs.forEach((tab) => {
      if (tab.id !== contextMenuTab.id) {
        handleTabClose(tab.id);
      }
    });
  };

  const handleCloseToTheLeft = (): void => {
    if (!contextMenuTab) return;
    const currentIndex = tabs.findIndex((tab) => tab.id === contextMenuTab.id);
    tabs.slice(0, currentIndex).forEach((tab) => handleTabClose(tab.id));
  };

  const handleCloseToTheRight = (): void => {
    if (!contextMenuTab) return;
    const currentIndex = tabs.findIndex((tab) => tab.id === contextMenuTab.id);
    tabs.slice(currentIndex + 1).forEach((tab) => handleTabClose(tab.id));
  };

  const handleDragEnd = (result: DropResult): void => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    reorderTabs(sourceIndex, destinationIndex);
  };

  // Tab title rendering with edit button
  const renderTabTitle = (tab: Tab): React.ReactNode => (
    <Space onContextMenu={(e) => handleContextMenu(e, tab)}>
      {tab.type === 'pipeline' ? (
        <PipelineIcon style={{ cursor: 'grab' }} />
      ) : tab.type === 'workflow' ? (
        <WorkflowIcon style={{ cursor: 'grab' }} />
      ) : tab.type === 'summary' ? (
        <InfoCircleOutlined style={{ cursor: 'grab' }} />
      ) : (
        <FileOutlined style={{ cursor: 'grab' }} />
      )}
      <span 
        style={{ color: tab.isModified ? token.colorPrimary : token.colorText }}
        data-testid="tab-name"
      >
        {tab.name}
      </span>
      <div
        onClick={(e) => {
          e.stopPropagation();
          setTabToRename(tab);
          setNewTabName(tab.name);
          setIsRenameModalVisible(true);
        }}
        style={{ cursor: 'pointer' }}
      >
        <EditOutlined />
      </div>
    </Space>
  );

  // Define menu items for the add tab dropdown
  const addTabMenuItems: MenuProps['items'] = [
    {
      key: 'pipeline',
      label: (
        <Space>
          <PipelineIcon />
          <span>{t('pipeline')}</span>
        </Space>
      ),
      onClick: () => handleAddTab('pipeline'),
    },
    {
      key: 'workflow',
      label: (
        <Space>
          <WorkflowIcon />
          <span>{t('workflow')}</span>
        </Space>
      ),
      onClick: () => handleAddTab('workflow'),
    },
    {
      key: 'markdown',
      label: (
        <Space>
          <FileOutlined />
          <span>{t('markdown')}</span>
        </Space>
      ),
      onClick: () => handleAddTab('markdown'),
    },
  ];

  const handleRename = (): void => {
    if (tabToRename && newTabName.trim()) {
      updateTabName(tabToRename.id, newTabName.trim());
      setIsRenameModalVisible(false);
      setTabToRename(null);
      setNewTabName('');
    }
  };

  const handleShowWelcome = (): void => {
    onShowWelcomeChange(true);
    // Clear active tab and pipeline when showing welcome screen
    clearActiveTab();
    // Clear active pipeline - setActivePipeline expects a string, so we'll handle this differently
    // Clear node selection state when showing welcome screen
    clearNodeSelectionState();
  };

  const renderTabContent = (tab: Tab): React.ReactNode => {
    // Show welcome if showWelcome is true
    if (showWelcome) {
      return <WelcomeViewer onCreateTab={handleAddTab} />;
    }

    // Only render content for the active tab to ensure container dimensions are available
    if (tab.id !== activeTabId) {
      return null; // Or a lightweight placeholder if needed
    }

    switch (tab.type) {
      case 'pipeline':
      case 'workflow':
        return (
          <div style={{ width: '100%', height: '100%' }}>
            <HopCanvasViewer pipelineId={tab.id} />
          </div>
        );
      case 'markdown': {
        const markdown = markdowns.find((md) => md.id === tab.id);
        return (
          <div style={{ width: '100%', height: '100%' }}>
            <MarkdownViewer tabId={tab.id} />
          </div>
        );
      }
      case 'summary': {
        const summary = summaries.find((s) => s.id === tab.id);
        if (!summary) {
          return <div>Summary not found</div>;
        }
        return (
          <div style={{ width: '100%', height: '100%' }}>
            <FileSummaryViewer tabId={tab.id} data={summary} />
          </div>
        );
      }
      default:
        return <div>Unknown tab type: {tab.type}</div>;
    }
  };

  const contextMenuItems: MenuProps['items'] = [
    {
      key: 'closeAll',
      label: 'Close All',
      onClick: handleCloseAll,
    },
    {
      key: 'closeAllButThis',
      label: 'Close All But This',
      onClick: handleCloseAllButThis,
    },
    {
      key: 'closeToTheLeft',
      label: 'Close to the Left',
      onClick: handleCloseToTheLeft,
    },
    {
      key: 'closeToTheRight',
      label: 'Close to the Right',
      onClick: handleCloseToTheRight,
    },
  ];

  return (
    <div style={tabsContainerStyle}>
      <TabsGlobalStyle />

      {tabs.length > 0 ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="tabs" direction="horizontal">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <Tabs
                  type="editable-card"
                  activeKey={showWelcome ? '' : activeTabId || ''}
                  onChange={handleTabChange}
                  tabBarExtraContent={{
                    left: (
                      <Button
                        type={showWelcome ? 'primary' : 'text'}
                        icon={<HomeOutlined />}
                        onClick={handleShowWelcome}
                        style={{
                          marginRight: '8px',
                          borderRadius: '6px',
                          height: '32px',
                        }}
                        title={t('home')}
                        role="button"
                        data-testid="home-button"
                      />
                    ),
                  }}
                  addIcon={
                    <Dropdown menu={{ items: addTabMenuItems }} trigger={['click']}>
                      <span data-testid="new-tab-button">
                        <PlusOutlined />
                      </span>
                    </Dropdown>
                  }
                  onEdit={(targetKey, action) => {
                    if (action === 'remove') {
                      handleTabClose(targetKey as string);
                    }
                    // Add action is handled by the Dropdown itself now
                  }}
                  items={tabs.map((tab, index) => ({
                    key: tab.id,
                    label: (
                      <Draggable draggableId={tab.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              ...provided.draggableProps.style,
                              display: 'inline-block',
                            }}
                          >
                            <Dropdown
                              menu={{ items: contextMenuItems }}
                              trigger={['contextMenu']}
                              open={contextMenuTab?.id === tab.id}
                              onOpenChange={(visible) => !visible && setContextMenuTab(null)}
                            >
                              {renderTabTitle(tab)}
                            </Dropdown>
                          </div>
                        )}
                      </Draggable>
                    ),
                    children: <div style={tabContentStyle}>{renderTabContent(tab)}</div>,
                    closable: true,
                  }))}
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  moreIcon={<EllipsisOutlined />}
                />
                {showWelcome && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '46px', // Height of tab bar
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: token.colorBgContainer,
                      zIndex: 10,
                    }}
                  >
                    <WelcomeViewer onCreateTab={handleAddTab} />
                  </div>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <WelcomeViewer onCreateTab={handleAddTab} />
      )}

      <Modal
        title={t('renameTab')}
        open={isRenameModalVisible}
        onOk={handleRename}
        onCancel={() => {
          setIsRenameModalVisible(false);
          setTabToRename(null);
        }}
      >
        <Input
          value={newTabName}
          onChange={(e) => setNewTabName(e.target.value)}
          placeholder={t('enterNewTabName')}
          autoFocus
        />
      </Modal>
    </div>
  );
};

export default TabManager;
